import { Device, Geotab } from "@fairfleet/geotab";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { CustomProperty } from "@/models/CustomProperty";

/**
 * Returns a copy of `device` where the custom property
 * "Mängelmeldung ignorieren" has its `value` set to the string
 * of `ignore`. If the property isn’t present, the device is returned
 * unchanged.
 */
function withIgnoreFlagToggled(device: Device, ignore: string): Device {
  return {
    ...device,
    customProperties: (device.customProperties as CustomProperty[]).map(prop =>
      prop.property.name === "Mängelmeldung ignorieren" ? { ...prop, value: ignore } : prop,
    ),
  };
}

async function updateDevice(geotabClient: Geotab, device: Device, ignoreMaintenance: string) {
  try {
    const result = await geotabClient.call("Set", {
      typeName: "Device",
      entity: withIgnoreFlagToggled(device, ignoreMaintenance),
    });
    return result;
  } catch (error) {
    console.error("Error updating device:", error);
  }
}

export function useUpdateDevice() {
  const queryClient = useQueryClient();
  const { geotabClient } = useGeotabClient();

  return useMutation({
    mutationFn: async (props: { device: Device; ignoreMaintenance: string }) =>
      await updateDevice(geotabClient, props.device, props.ignoreMaintenance),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["devicesBySearch"],
      });
    },
  });
}
